/**
 * Utilities for creating stable selector references to avoid re-renders.
 * These help with preserving object identity when the underlying data hasn't changed.
 */

/**
 * Creates a WeakMap-based cache for memoizing objects by reference.
 * @returns A function that returns a memoized reference to an object
 */
export function createReferenceCache<T extends object>() {
  const cache = new WeakMap<T, T>();
  
  return function getStableReference(obj: T): T {
    // Return the object itself if not an object or null/undefined
    if (!obj || typeof obj !== 'object') return obj;
    
    const cached = cache.get(obj);
    if (cached) return cached;
    
    // Store and return the new object
    cache.set(obj, obj);
    return obj;
  };
}

/**
 * Creates a function that returns a stable reference to bounds objects with the same values.
 * @returns A function that returns a memoized reference to a bounds object
 */
export function createBoundsCache() {
  const cache = new Map<string, google.maps.LatLngBoundsLiteral>();
  
  return function getStableBounds(bounds: google.maps.LatLngBoundsLiteral | null): google.maps.LatLngBoundsLiteral | null {
    if (!bounds) return null;
    
    // Create a cache key from the bounds values
    const key = `${bounds.north},${bounds.east},${bounds.south},${bounds.west}`;
    
    // Check if we already have a reference for these values
    const cached = cache.get(key);
    if (cached) return cached;
    
    // Store and return the new bounds
    cache.set(key, bounds);
    return bounds;
  };
}

/**
 * Creates a function that returns a stable reference to an array if its items are unchanged.
 * @returns A function that returns a memoized reference to an array
 */
export function createArrayCache<T>() {
  let prevItems: T[] | null = null;
  
  return function getStableArray(items: T[]): T[] {
    if (!prevItems || prevItems.length !== items.length) {
      prevItems = items;
      return items;
    }
    
    // Check if items are the same
    const isEqual = prevItems.every((item, index) => item === items[index]);
    if (isEqual) {
      return prevItems; // Return the previous reference
    }
    
    // Store and return the new array
    prevItems = items;
    return items;
  };
} 